// Logbook rules, ported from Logbook's own app (gajananhs/logbook
// assets/js/app.js and import-excel.js) so the HR module behaves the same.
// Pure functions only — the data comes from /api/hr/logbook/data
// (backend/hr_logbook.py), already scoped to what the user may see.

export const ROLE_LABEL = { dept_head: "Department Head", sub_head: "Sub-department Head", employee: "Employee" };
export const orgRoleLabel = (role) => ROLE_LABEL[role] || "Employee";

export const REPEAT_LABEL = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" };

export function statusById(statuses, id) {
  return statuses.find((s) => s.id === id) || { id, name: "Unknown", color: "#8B94A0", isDone: false };
}
export const isTaskDone = (statuses, t) => !!statusById(statuses, t.status).isDone;

export const totalHoursLogged = (t) => (t.updates || []).reduce((s, u) => s + (Number(u.hoursLogged) || 0), 0);

export function latestProgress(statuses, t) {
  if (isTaskDone(statuses, t)) return 100;
  const ups = t.updates || [];
  return ups.length ? Number(ups[ups.length - 1].progressPct) || 0 : 0;
}

export function efficiencyPct(statuses, t) {
  if (!isTaskDone(statuses, t)) return null;
  const actual = t.actualHours || totalHoursLogged(t) || 0;
  if (!t.estimatedHours || !actual) return null;
  return Math.max(0, Math.min(150, Math.round((t.estimatedHours / actual) * 100)));
}

const parseTs = (s) => new Date(String(s || "").replace(" ", "T"));

export function isOverdue(statuses, t) {
  if (isTaskDone(statuses, t) || !t.dueDate) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(t.dueDate.length === 10 ? `${t.dueDate}T00:00:00` : t.dueDate);
  return due < today;
}

// No update for 3+ days → "Needs update".
export function isStale(statuses, t) {
  if (isTaskDone(statuses, t)) return false;
  const ups = t.updates || [];
  const last = ups.length ? parseTs(ups[ups.length - 1].date) : parseTs(t.createdAt);
  return Math.round((new Date() - last) / 86400000) >= 3;
}

export const isScheduledForLater = (t, today) => !!t.availableFrom && t.availableFrom > today;

// Newest-assigned first: created, or for a scheduled task the day it went
// live, whichever is later; ties fall back to the earliest due date.
function assignedAtMs(t) {
  const c = parseTs(t.createdAt).getTime() || 0;
  const a = t.availableFrom ? new Date(`${t.availableFrom}T00:00:00`).getTime() : 0;
  return Math.max(c, a);
}
export function sortNewestFirst(list) {
  return list.slice().sort((a, b) => assignedAtMs(b) - assignedAtMs(a)
    || new Date(a.dueDate || "2999-12-31") - new Date(b.dueDate || "2999-12-31")
    || String(b.id).localeCompare(String(a.id)));
}

export const avg = (xs) => (xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null);

export function effColor(pct) {
  if (pct === null || pct === undefined) return "#94a3b8";
  if (pct >= 90) return "#16a34a";
  if (pct >= 70) return "#d97706";
  return "#dc2626";
}

// Who `me` may see on the Team board (Logbook's visibleTeamUserIds).
export function visibleTeamUserIds(me, users) {
  if (me.isAdmin) return users.map((u) => u.id);
  if (me.orgRole === "dept_head") return users.filter((u) => me.departmentId && u.departmentId === me.departmentId).map((u) => u.id);
  if (me.orgRole === "sub_head") return users.filter((u) => me.subDepartmentId && u.subDepartmentId === me.subDepartmentId).map((u) => u.id);
  return [me.id];
}
export const canSeeTeamBoard = (me) => !!me && (me.isAdmin || me.orgRole === "dept_head" || me.orgRole === "sub_head");

// Who `me` may assign to (Logbook's assignableUsersFor; the server checks again).
export function assignableUsersFor(me, users) {
  if (me.isAdmin) return users;
  if (me.orgRole === "dept_head") return users.filter((u) => u.id === me.id || (me.departmentId && u.departmentId === me.departmentId));
  if (me.orgRole === "sub_head") return users.filter((u) => u.id === me.id || (me.subDepartmentId && u.subDepartmentId === me.subDepartmentId));
  return users.filter((u) => u.id === me.id);
}

export function orgContextLabel(u, departments, subDepartments) {
  const dept = departments.find((d) => d.id === u.departmentId)?.name;
  const sub = subDepartments.find((s) => s.id === u.subDepartmentId)?.name;
  if (u.orgRole === "dept_head") return dept ? `Head of ${dept}` : "Department Head (no department set)";
  if (u.orgRole === "sub_head") return sub ? `Head of ${sub}${dept ? ` (${dept})` : ""}` : "Sub-department Head (no sub-department set)";
  if (sub) return `${sub}${dept ? ` · ${dept}` : ""}`;
  return dept || "No department set";
}

// Forgiving name search: case, spaces and punctuation don't matter, words in
// any order; names starting with the text come first.
const searchNorm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
export function teamSearchMatches(query, users) {
  const q = searchNorm(query);
  if (!q) return [];
  const words = q.split(" ");
  const squashed = q.replace(/ /g, "");
  return users
    .map((u) => {
      const name = searchNorm(u.name);
      const hit = words.every((w) => name.includes(w)) || name.replace(/ /g, "").includes(squashed);
      if (!hit) return null;
      const rank = name.indexOf(q) === 0 ? 0 : (name.split(" ").some((p) => p.indexOf(words[0]) === 0) ? 1 : 2);
      return { u, rank };
    })
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank || a.u.name.localeCompare(b.u.name))
    .map((x) => x.u);
}

export function addRepeat(start, type) {
  if (!start) return "";
  const d = new Date(`${start}T00:00:00`);
  if (type === "daily") d.setDate(d.getDate() + 1);
  else if (type === "weekly") d.setDate(d.getDate() + 7);
  else if (type === "monthly") d.setMonth(d.getMonth() + 1);
  return isoDate(d);
}

const pad = (n) => String(n).padStart(2, "0");
export const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(String(iso).length === 10 ? `${iso}T00:00:00` : String(iso).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
export function fmtDateTime(iso) {
  const d = parseTs(iso);
  if (Number.isNaN(d.getTime())) return String(iso || "");
  return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

export function hexToRgba(hex, alpha) {
  const h = String(hex || "#8B94A0").replace("#", "");
  const r = parseInt(h.substring(0, 2), 16), g = parseInt(h.substring(2, 4), 16), b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Report period chips.
export function reportPeriod(period) {
  const today = new Date();
  if (period === "day") return [isoDate(today), isoDate(today)];
  if (period === "week") {
    const day = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() + (day === 0 ? -6 : 1 - day));
    return [isoDate(monday), isoDate(today)];
  }
  if (period === "month") return [isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), isoDate(today)];
  return ["", ""];
}

export const REPORT_TYPES = [
  { value: "task_summary", label: "Task Summary" },
  { value: "department_kpi", label: "All Departments Report" },
];
export const reportTypeLabel = (type) => REPORT_TYPES.find((r) => r.value === type)?.label || "Task Summary";

// Plain-text summaries for WhatsApp / e-mail sharing.
export function teamSummaryText({ users, tasks, statuses }) {
  const open = tasks.filter((t) => !isTaskDone(statuses, t)).length;
  const overdue = tasks.filter((t) => isOverdue(statuses, t)).length;
  const eff = avg(tasks.map((t) => efficiencyPct(statuses, t)).filter((v) => v !== null));
  let body = `Total tasks: ${tasks.length}\nOpen: ${open}\nOverdue: ${overdue}\nAvg efficiency: ${eff === null ? "—" : `${eff}%`}\n\n`;
  users.slice(0, 15).forEach((u) => {
    const ut = tasks.filter((t) => t.assignedTo === u.id);
    const o = ut.filter((t) => !isTaskDone(statuses, t)).length;
    const od = ut.filter((t) => isOverdue(statuses, t)).length;
    body += `${u.name}: ${o} open${od ? `, ${od} overdue` : ""}\n`;
  });
  return body;
}
export function reportShareText(data) {
  const isDept = data.rows?.[0] && "department" in data.rows[0];
  const range = data.rangeStart || data.rangeEnd ? `${data.rangeStart || "…"} to ${data.rangeEnd || "…"}` : "All time";
  const lines = (data.rows || []).map((r) => (isDept
    ? `${r.department} (${r.headcount}): ${r.completed}/${r.total} done, ${r.overdue} overdue, ${r.avgEfficiency === null ? "—" : `${r.avgEfficiency}%`} eff`
    : `${r.name}: ${r.completed}/${r.assigned} done, ${r.avgEfficiency === null ? "—" : `${r.avgEfficiency}%`} eff`)).join("\n");
  return { title: `LOGBOOK ${reportTypeLabel(data.type)}`, range, lines };
}

export const whatsappUrl = (text) => `https://wa.me/?text=${encodeURIComponent(text)}`;
export const mailUrl = (subject, body) => `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

/* ---------------- Excel import (Team board, Logbook admins) ----------------
   The Daily Production Meeting workbook: RESPONSIBILITY → employee(s), the
   second "date" column → target date, the other columns → task details. */
const importNorm = (s) => String(s == null ? "" : s).toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
const headerNorm = (s) => String(s == null ? "" : s).toLowerCase().replace(/\s+/g, " ").trim();
const cellText = (cell) => {
  if (!cell || cell.v === undefined || cell.v === null) return "";
  return String(cell.w !== undefined && cell.w !== null ? cell.w : cell.v).trim();
};
function validDate(y, m, d) {
  if (!(y >= 2000 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return false;
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}
// Sheet dates are written day-month-year; a cell Excel turned into a real
// date was read month-first, so swap it back (and flag it in the preview).
export function importResolveTarget(XLSX, cell) {
  if (!cell || cell.v === undefined || cell.v === null || String(cell.v).trim() === "") return null;
  if (cell.t === "n" && cell.z && XLSX.SSF.is_date(cell.z)) {
    const dc = XLSX.SSF.parse_date_code(cell.v);
    if (!dc) return { error: "Unreadable date", raw: cellText(cell) };
    let { y, m, d } = dc;
    let fixed = false;
    if (d <= 12 && m !== d) { const nm = d; d = m; m = nm; fixed = true; }
    if (!validDate(y, m, d)) return { error: "Unreadable date", raw: cellText(cell) };
    return { iso: `${y}-${pad(m)}-${pad(d)}`, display: `${pad(d)}-${pad(m)}-${y}`, fixed, raw: cellText(cell) };
  }
  const txt = cellText(cell);
  const mt = txt.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
  if (mt) {
    const d = +mt[1], m = +mt[2];
    let y = +mt[3];
    if (y < 100) y += 2000;
    if (validDate(y, m, d)) return { iso: `${y}-${pad(m)}-${pad(d)}`, display: `${pad(d)}-${pad(m)}-${y}`, fixed: false, raw: txt };
  }
  return { error: "Unreadable date", raw: txt };
}
export function importParseWorksheet(XLSX, ws) {
  if (!ws || !ws["!ref"]) return { error: "This sheet is empty." };
  const range = XLSX.utils.decode_range(ws["!ref"]);
  const cellAt = (r, c) => ws[XLSX.utils.encode_cell({ r, c })];
  let hdr = -1;
  for (let r = range.s.r; r <= Math.min(range.e.r, range.s.r + 15) && hdr < 0; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      if (headerNorm(cellText(cellAt(r, c))) === "responsibility") { hdr = r; break; }
    }
  }
  if (hdr < 0) return { error: "This sheet has no RESPONSIBILITY column, so it can't be imported." };
  const cols = { date: [] };
  const names = { "to no": "toNo", "customer name": "customer", "part number": "part", "ord. qty": "qty", "b/l": "bl", remarks: "remarks", responsibility: "resp" };
  for (let c = range.s.c; c <= range.e.c; c++) {
    const h = headerNorm(cellText(cellAt(hdr, c)));
    if (h === "date") cols.date.push(c);
    else if (names[h] && cols[names[h]] === undefined) cols[names[h]] = c;
  }
  if (cols.date.length < 2) return { error: "Couldn't find both the DATE column and the target \"date\" column on this sheet." };
  const orderDate = cols.date[0];
  const target = cols.date[cols.date.length - 1];
  const text = (r, c) => (c === undefined ? "" : cellText(cellAt(r, c)));
  const rows = [];
  const fixedDates = {};
  for (let r = hdr + 1; r <= range.e.r; r++) {
    const rec = {
      rowNum: r + 1, toNo: text(r, cols.toNo), customer: text(r, cols.customer), part: text(r, cols.part),
      qty: text(r, cols.qty), bl: text(r, cols.bl), orderDate: text(r, orderDate), remarks: text(r, cols.remarks),
      respRaw: text(r, cols.resp), target: null, targetError: "",
    };
    const tr = importResolveTarget(XLSX, cellAt(r, target));
    if (tr && tr.error) rec.targetError = tr.raw;
    else if (tr) { rec.target = tr; if (tr.fixed) fixedDates[tr.display] = true; }
    if (!rec.toNo && !rec.customer && !rec.part && !rec.respRaw && !tr) continue;
    rows.push(rec);
  }
  return { rows, fixedDates: Object.keys(fixedDates) };
}
export const importSplitNames = (raw) => String(raw || "").split(/\s*(?:\/|,|&|\+|\band\b)\s*/i).map((s) => s.trim()).filter(Boolean);
// Only an unambiguous match is pre-selected — never a guess between two people.
export function importAutoMatch(token, users) {
  const t = importNorm(token);
  if (!t) return "";
  let hit = users.filter((u) => importNorm(u.name) === t);
  if (hit.length === 1) return hit[0].id;
  if (hit.length > 1) return "";
  const tw = t.split(" ");
  hit = users.filter((u) => { const w = importNorm(u.name).split(" "); return tw.every((x) => w.includes(x)); });
  return hit.length === 1 ? hit[0].id : "";
}
export function importBuildTokens(parsed, users) {
  const map = {};
  const order = [];
  parsed.rows.forEach((r) => {
    if (!r.target) return;
    importSplitNames(r.respRaw).forEach((tok) => {
      const key = importNorm(tok);
      if (!key) return;
      if (!map[key]) { map[key] = { key, label: tok, count: 0, userId: importAutoMatch(tok, users) }; order.push(key); }
      map[key].count++;
    });
  });
  return order.map((k) => map[k]);
}
function importBuildTask(r, employeeId) {
  const title = [r.toNo, r.part, r.customer].filter(Boolean).join(" · ").slice(0, 200) || `Row ${r.rowNum}`;
  const lines = [];
  const add = (label, v) => { if (v) lines.push(`${label}: ${v}`); };
  add("DATE", r.orderDate); add("TO NO", r.toNo); add("CUSTOMER NAME", r.customer); add("PART NUMBER", r.part);
  add("ORD. QTY", r.qty); add("B/L", r.bl); add("TARGET DATE", r.target.display);
  add("REMARKS", r.remarks); add("RESPONSIBILITY", r.respRaw);
  return { employeeId, targetDate: r.target.iso, title, description: lines.join("\n") };
}
export function importBuildPlan(parsed, tokens) {
  const mapping = Object.fromEntries(tokens.map((t) => [t.key, t.userId]));
  const stat = { rows: parsed.rows.length, noDate: 0, noEmployee: 0, notMatchedRows: 0 };
  const tasks = [];
  const occ = {};
  const byDate = {};
  parsed.rows.forEach((r) => {
    if (!r.target) { stat.noDate++; return; }
    const names = importSplitNames(r.respRaw);
    if (!names.length) { stat.noEmployee++; return; }
    const ids = [];
    names.forEach((n) => { const id = mapping[importNorm(n)]; if (id && !ids.includes(id)) ids.push(id); });
    if (!ids.length) { stat.notMatchedRows++; return; }
    ids.forEach((id) => {
      const t = importBuildTask(r, id);
      const base = [r.toNo, r.part, r.customer].join("|").toLowerCase();
      const k = `${id}|${t.targetDate}|${base}`;
      occ[k] = (occ[k] || 0) + 1;          // identical rows in one sheet each import once
      t.basis = `${base}#${occ[k]}`;
      tasks.push(t);
      byDate[t.targetDate] = (byDate[t.targetDate] || 0) + 1;
    });
  });
  return { tasks, stat, byDate };
}
